/**
 * Is this SPA running on a Company portal (ADR-024), and where is fias.io?
 *
 * A LEAF module (imports nothing), so `api-base.ts` and the rest of the client
 * can depend on it without cycles.
 *
 * Detection is POSITIVE for the platform: a pinned list of the platform's own
 * hostnames (plus the build's `VITE_SITE_ORIGIN`). Anything else is a portal —
 * the same SPA bundle, served through the portal edge. Getting this wrong in
 * the platform direction is harmless (a portal would try the platform API and
 * be refused); getting it wrong in the portal direction on a platform host
 * would point API calls at the SPA origin. Since any UNLISTED host counts as a
 * portal, the list must name every platform host: that is what
 * tests/architecture/company-web-mirrors.test.ts pins against the CDK's
 * domain config.
 * The portal's host-profile response confirms the portal and supplies its
 * brand; nothing is decided from this module alone except where the API is.
 *
 * SSR-safe: with no `window` (the prerender), this is never a portal.
 */

const PINNED_PLATFORM_HOSTNAMES = new Set([
  'fias.io',
  'www.fias.io',
  'platform.fias.io',
  'staging.fias.io',
  'localhost',
  '127.0.0.1',
]);

function hostnameOf(origin: string | undefined): string | null {
  if (!origin) return null;
  try {
    return new URL(origin).hostname.toLowerCase();
  } catch {
    return null;
  }
}

const SITE_ORIGIN: string | undefined = import.meta.env.VITE_SITE_ORIGIN;
const SITE_HOSTNAME = hostnameOf(SITE_ORIGIN);

export function isPlatformHostname(hostname: string): boolean {
  const h = hostname.toLowerCase();
  return (
    PINNED_PLATFORM_HOSTNAMES.has(h) ||
    h === SITE_HOSTNAME ||
    // Codespaces preview hosts and bare IPs are always developer machines.
    h.endsWith('.app.github.dev') ||
    /^\d{1,3}(\.\d{1,3}){3}$/.test(h)
  );
}

/** True when this page is a Company portal. Fixed for the life of the page. */
export const IS_COMPANY_PORTAL: boolean =
  typeof window !== 'undefined' && !isPlatformHostname(window.location.hostname);

/**
 * The platform's canonical origin — fias.io — for every link that leaves the
 * current page or carries a token: share links, invites, booking and signing
 * links, the embed snippet, and the portal's sign-in hand-off. Never the
 * portal's own origin: a link must outlive the portal and must not hand a
 * token to a host a Company's DNS controls (T15).
 *
 * Local development: a portal on `<name>.localhost:<port>` hands off to
 * `http://localhost:<port>`, the local fias.io.
 */
export function shareOrigin(): string {
  if (SITE_ORIGIN) return SITE_ORIGIN.replace(/\/+$/, '');
  if (typeof window === 'undefined') return 'https://fias.io';
  const { hostname, protocol, port } = window.location;
  if (hostname.endsWith('.localhost')) return `${protocol}//localhost${port ? `:${port}` : ''}`;
  return IS_COMPANY_PORTAL ? 'https://fias.io' : window.location.origin;
}
